import { defineStore } from 'pinia';
import axios from 'axios';
import { ref } from 'vue';
import * as Sentry from '@sentry/vue';
import { useVapCoreFor } from '@/services/backend';
import { fetchBranding } from '@/services/vapcore/branding';

export const useBrandingStore = defineStore('branding', {
  state: () => ({
    brand: ref({}),
    brandCSS: ref({}),
    // Whether a load has been attempted this page load, successful or not. Without it a failed
    // load leaves `brand` empty, and anything keyed off "is the brand empty" asks again on every
    // single navigation.
    brandAttempted: false,
  }),
  actions: {
    /**
     * Loads the brand and its theme for a domain, from whichever backend is configured to serve
     * branding. Both paths end up populating the same two pieces of state, so nothing downstream
     * knows or cares which one ran.
     */
    async fetchBrand(brandingDomain) {
      this.brandAttempted = true;

      const backend = useVapCoreFor('branding') ? 'VapCore' : 'iGard';

      if (backend === 'VapCore') {
        await this._fetchBrandFromVapCore(brandingDomain);
      } else {
        await this._fetchBrandFromIGard(brandingDomain);
      }

      // Which backend served it, and whether anything came back. A brand that loads but arrives
      // empty looks identical on screen to one that never loaded at all.
      console.info(
        `[branding] ${backend} served ${brandingDomain}: ` +
          `${Object.keys(this.brand).length} brand fields, ` +
          `${Object.keys(this.brandCSS).length} theme properties`
      );
    },

    /** VapCore returns the brand and its theme together, in one request. */
    async _fetchBrandFromVapCore(brandingDomain) {
      try {
        const { brand, brandCSS } = await fetchBranding(brandingDomain);

        this.brand = brand;
        this.applyBrandCSS(brandCSS);
      } catch (error) {
        this.reportBrandingFailure(brandingDomain, error);
      }
    },

    /** iGard serves the brand and its theme from two separate endpoints. */
    async _fetchBrandFromIGard(brandingDomain) {
      try {
        const [brandResponse, cssResponse] = await Promise.all([
          axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/getBrand?domain=${brandingDomain}`
          ),
          axios.get(
            `${process.env.VUE_APP_API_BASE_URL}/getBrandingCSS?domain=${brandingDomain}`
          ),
        ]);

        this.brand = brandResponse.data.data;
        this.applyBrandCSS(
          (cssResponse.data.data || []).reduce((styles, item) => {
            styles[item.css_property] = item.css_value;
            return styles;
          }, {})
        );
      } catch (error) {
        this.reportBrandingFailure(brandingDomain, error);
      }
    },

    /**
     * Assigned key by key so the reactive object components already hold a reference to is
     * updated in place, rather than being swapped for a new one.
     */
    applyBrandCSS(styles) {
      Object.entries(styles).forEach(([property, value]) => {
        this.brandCSS[property] = value;
      });
    },

    /**
     * An unbranded portal is still usable, so a missing brand must not stop the app starting —
     * this runs in the router guard before every page. It falls back to the stylesheet defaults.
     */
    reportBrandingFailure(brandingDomain, error) {
      Sentry.captureException(error);
      Sentry.captureMessage(
        `Could not load branding for ${brandingDomain}: ${error.message}`
      );
    },
  },
});
